export interface ConsoleLogsSettings {
  indexPage: {
    componentRendering: boolean;
    authFlow: boolean;
    networkStatus: boolean;
    termsCheck: boolean;
    errorHandling: boolean;
    performanceMetrics: boolean;
  };
  performance: {
    performanceMonitor: boolean;
    fontLoading: boolean;
  };
  auth: {
    authContext: boolean;
  };
  admin: {
    useAdminSettings: boolean;
  };
  debugging: {
    enhancedLogging: boolean;
    logFunctionDuration: boolean;
  };
  diagnostics: {
    iconMapping: boolean;
    imageTransform: boolean;
  };
}

export interface AuthVerificationData {
  sessionId?: string;
  timestamp: string;
  lastUpdated: string;
  url: string;
  token: string;
  tokenVerified: boolean;
  user: {
    id: string;
    email: string;
    displayName: string;
    role?: string;
  } | null;
  profileUpdated: boolean;
  sessionCreated: boolean;
  sessionEmail: string;
  sessionDisplayName: string;
  steps?: Array<{
    step: string;
    timestamp: string;
    details?: any;
  }>;
  error?: {
    message: string;
    code: string | number;
    stack?: string;
  };
}

export interface AdminSettings {
  notifications: {
    emailEnabled: boolean;
    smsEnabled: boolean;
    whatsappEnabled: boolean;
    signups: boolean;
    bookings: boolean;
    cancellations: boolean;
    reviews: boolean;
  };
  email: {
    admin: string;
    test: string;
    copyToAdmin: boolean;
  };
  smtp: {
    host: string;
    port: number;
    secure: boolean;
    username: string;
    password: string;
    fromEmail: string;
    fromName: string;
  };
  whatsapp: {
    enabled: boolean;
    adminPhone: string;
    templates: {
      booking: string;
      cancellation: string;
      review: string;
      signup: string;
    };
  };
  sms: {
    enabled: boolean;
    provider: string;
    apiKey: string;
    fromNumber: string;
    adminPhone: string;
    partnerID: string;
    shortcode: string;
    endpoint: string;
    callbackUrl: string;
    notifications: {
      signups: boolean;
      bookings: boolean;
      cancellations: boolean;
      reviews: boolean;
    };
    templates: {
      booking: string;
      cancellation: string;
      review: string;
      signup: string;
    };
  };
  calendar: {
    defaultDuration: number;
    bufferTime: number;
    workingHours: {
      start: string;
      end: string;
    };
    workingDays: number[];
    advanceBookingDays: number;
  };
  calendar_defaults: {
    business_hours: Array<{
      daysOfWeek: number[];
      startTime: string;
      endTime: string;
    }>;
    time_range: {
      minTime: string;
      maxTime: string;
    };
    slot_duration: string;
    initial_view: string;
    mobile_view: string;
  };
  brand: {
    colors: {
      color1: string;
      color2: string;
      color3: string;
      color4: string;
      color5: string;
    };
    lastUpdated: string;
  };
  aboutUs: {
    content: string;
    lastUpdated: string;
  };
  vendorHelp: {
    content: string;
    lastUpdated: string;
  };
  testing: {
    enabled: boolean;
  };
  display: {
    layoutMode: 'uniform' | 'adaptive';
  };
  pwa: {
    vapidKeys: {
      publicKey: string;
      privateKey: string;
    };
    customIcons: {
      icon72: string;
      icon96: string;
      icon120: string;
      icon128: string;
      icon144: string;
      icon152: string;
      icon180: string;
      icon192: string;
      icon384: string;
      icon512: string;
      appleTouchIcon: string;
    };
    manifest: {
      name: string;
      shortName: string;
      description: string;
      themeColor: string;
      backgroundColor: string;
      display?: string;
      orientation?: string;
      icons?: {
        icon192?: string;
        icon512?: string;
        appleTouchIcon?: string;
      };
    };
  };
  push: {
    enabled: boolean;
    notifications: {
      signups: boolean;
      bookings: boolean;
    };
  };
  consoleLogs: ConsoleLogsSettings;
}

export const defaultSettings: AdminSettings = {
  notifications: {
    emailEnabled: true,
    smsEnabled: false,
    whatsappEnabled: false,
    signups: true,
    bookings: true,
    cancellations: true,
    reviews: true,
  },
  email: {
    admin: 'info@lamulamu.com',
    test: 'info@lamulamu.com',
    copyToAdmin: false,
  },
  smtp: {
    host: '',
    port: 587,
    secure: false,
    username: '',
    password: '',
    fromEmail: '',
    fromName: 'LamuLamu',
  },
  whatsapp: {
    enabled: false,
    adminPhone: '',
    templates: {
      booking: 'New booking: {customerName} for {serviceName} on {date} at {time}',
      cancellation: 'Booking cancelled: {customerName} for {serviceName} on {date} at {time}',
      review: 'New review: {rating} stars for {serviceName} from {customerName}',
      signup: 'New user signed up: {userName} ({userEmail})',
    },
  },
  sms: {
    enabled: false,
    provider: 'twilio',
    apiKey: '',
    fromNumber: '',
    adminPhone: '',
    partnerID: '13693',
    shortcode: 'TextSMS',
    endpoint: 'regular',
    callbackUrl: '',
    notifications: {
      signups: true,
      bookings: true,
      cancellations: true,
      reviews: true,
    },
    templates: {
      booking: 'New booking: {customerName} for {serviceName} on {date} at {time}',
      cancellation: 'Booking cancelled: {customerName} for {serviceName} on {date} at {time}',
      review: 'New review: {rating} stars for {serviceName}',
      signup: 'New user signed up: {userName} ({userEmail})',
    },
  },
  calendar: {
    defaultDuration: 60,
    bufferTime: 15,
    workingHours: {
      start: '09:00',
      end: '17:00',
    },
    workingDays: [1, 2, 3, 4, 5],
    advanceBookingDays: 30,
  },
  calendar_defaults: {
    business_hours: [{
      daysOfWeek: [1, 2, 3, 4, 5],
      startTime: '09:00',
      endTime: '17:00'
    }],
    time_range: {
      minTime: '08:00:00',
      maxTime: '20:00:00'
    },
    slot_duration: '00:30:00',
    initial_view: 'timeGridDay',
    mobile_view: 'timeGridDay'
  },
  brand: {
    colors: {
      color1: "#165a54",
      color2: "#23643D",
      color3: "#91DA73",
      color4: "#0D2D23",
      color5: "#F5FBEF",
    },
    lastUpdated: new Date().toISOString(),
  },
  aboutUs: {
    content: '',
    lastUpdated: new Date().toISOString(),
  },
  vendorHelp: {
    content: '<h1>Vendor Help</h1><p>Welcome to the vendor portal. Here you can manage your services, bookings, and calendar.</p>',
    lastUpdated: new Date().toISOString(),
  },
  testing: {
    enabled: false,
  },
  display: {
    layoutMode: 'adaptive',
  },
  pwa: {
    vapidKeys: {
      publicKey: '',
      privateKey: '',
    },
    customIcons: {
      icon72: '',
      icon96: '',
      icon120: '',
      icon128: '',
      icon144: '',
      icon152: '',
      icon180: '',
      icon192: '',
      icon384: '',
      icon512: '',
      appleTouchIcon: '',
    },
    manifest: {
      name: 'LamuLamu - Local Services Platform',
      shortName: 'LamuLamu',
      description: 'Find and book local services in your area',
      themeColor: '#8B5CF6',
      backgroundColor: '#ffffff',
      display: 'standalone',
      orientation: 'portrait',
    },
  },
  push: {
    enabled: false,
    notifications: {
      signups: false,
      bookings: false,
    },
  },
  consoleLogs: {
    indexPage: {
      componentRendering: false,
      authFlow: false,
      networkStatus: false,
      termsCheck: false,
      errorHandling: true,
      performanceMetrics: false,
    },
    performance: {
      performanceMonitor: false,
      fontLoading: false,
    },
    auth: {
      authContext: false,
    },
    admin: {
      useAdminSettings: false,
    },
    debugging: {
      enhancedLogging: false,
      logFunctionDuration: false,
    },
    diagnostics: {
      iconMapping: false,
      imageTransform: false,
    },
  },
};
